import {gql, useQuery} from '@apollo/client';
import React, {useState} from 'react';
import {LoadingSpinner, Paths, RelayConnection, SimpleDropdown, ErrorDisplay} from "../../../common";
import {
    AuthorizationColumn,
    AuthorizationInfo, AuthorizationSortBy,
    SortOrder,
} from "../domain";
import {RelayPagination, RelayPaginationState, useRelayPagination} from "../../pagination";
import {CaretDownIcon, CaretUpIcon, Icon, Pane} from "evergreen-ui";
import {AuthorizationPreview} from "./AuthorizationPreview";
import {AuthorizationListAction, AuthorizationListFilterValue} from "./authorizationListReducer";
import {NavigationSearchBar} from "../../../placement/components/NavigationSearchBar";
import {asQueryParam, AuthorizationListFilter} from "./AuthorizationListFilter";
import {AuthorizationListFilterBadge} from "./AuthorizationListFilterBadge";
import {ExportButton} from "common/component/ExportButton";
import {useObservable} from "rxsupport";
import {useExportData} from "../export/useExportData";
import {ExportRequestModal} from "../export/ExportRequestModal";

export const AUTHORIZATION_LIST = gql`
query authorizationsQuery($first: Int = 20, $after: String, $params: AuthorizationQueryParamsInput!, $search: String) {
    authorizationsQuery(
        first: $first
        after: $after
        params: $params
        search: $search
    ) {
        edges {
            cursor
            node {
                guid
                sfId
                recordType
                client {
                    guid
                    fullName
                }
               
                status 
                bhpnAuthNumber
                diagnosis
                serviceLine
                funderServiceType
                funderAuthorizationRecordType
                reportDueDate
                startDate
                endDate
                isAtRisk

                allServiceType
                latestSubmittedClientReport {
                    guid
                    submittedAt
                    reportStatus 
                    reportType
                    sfOperationCaseNumber
                    sfCloseReason
                    sfCloseDate
                }
                latestSubmittedClientReportByReportType {
                    guid
                    reportType
                    authorizedDate
                    reportStatus
                    sfOperationCaseNumber
                    submittedAt
                    sfCloseReason
                    sfCloseDate
                }
            }
        }
        totalElements
        pageInfo {
            hasPreviousPage
            endCursor
            hasNextPage
            startCursor
        }
    }
}
 `

// Accept-and-ack export trigger (BHPNPP-3084). Carries the grid's current filters; the file is
// generated asynchronously. Reused via useExportData; future grids add their own gated mutation.
export const EXPORT_AUTHORIZATIONS = gql`
    mutation ($filters: String) {
        exportAuthorizations(filters: $filters) {
            guid
            status
        }
    }
`;

export type AuthorizationListQueryResponse = RelayConnection<AuthorizationInfo> & { totalElements: number };

export const AuthorizationListColumn: Readonly<AuthorizationColumn[]> = [
    {name: 'Authorization Number', attr: 'bhpnAuthNumber', span: 2},
    {name: 'Client Name', attr: 'client.fullName', span: 2},
    {name: 'Type', attr: 'recordType', span: 2},
    {name: 'Service', attr: 'allServiceType', span: 2},
    {name: 'Start Date', attr: 'startDate', span: 2},
    {name: 'End Date', attr: 'endDate', span: 2},
    {name: 'Status', attr: 'status', span: 1},
    {name: 'Report Due Date', attr: 'reportDueDate', span: 2},
    {name: 'Report Submitted Date', attr: 'latestSubmittedClientReport.submittedAt', span: 2},
    {name: 'Report Status', attr: 'latestSubmittedClientReport.reportStatus', span: 2},
]

type AuthorizationListProps = {
    selectedAuthorization: AuthorizationInfo | null;
    sortBy: AuthorizationSortBy;
    pagination: RelayPaginationState;
    filterValue: AuthorizationListFilterValue,
    pageSize: number;
    dispatch: React.Dispatch<AuthorizationListAction>;
    searchText: string;
}


export const AuthorizationList = ({
                                   selectedAuthorization,
                                   sortBy,
                                   pagination,
                                   dispatch,
                                   filterValue,
                                   searchText,
                                   pageSize
                               }: AuthorizationListProps) => {
    const {loading, data, error} = useQuery<{ authorizationsQuery: AuthorizationListQueryResponse }, {}>(
        AUTHORIZATION_LIST, {
            variables: {
                ...pagination.toGraphQlParams(),
                search: searchText,
                params: {
                    ...asQueryParam(filterValue),
                    sortDir: sortBy.order,
                    sortBy: sortBy.column,
                },
            }
        }
    );

    const {isRunning$: isExporting$, execute: exportData} = useExportData(EXPORT_AUTHORIZATIONS);
    const isExporting = useObservable(isExporting$, false);
    const [exportGuid, setExportGuid] = useState<string | null>(null);
    // Export the current filtered view — sends the same filter snapshot the grid renders, then
    // opens the progress modal to poll for and download the generated file.
    const handleExport = async () => {
        const guid = await exportData(JSON.stringify({
            ...asQueryParam(filterValue),
            search: searchText,
            sortDir: sortBy.order,
            sortBy: sortBy.column
        }));
        if (guid) {
            setExportGuid(guid);
        }
    };

    const setSortByColumn = (newSortColumn: Paths<AuthorizationInfo>) => {
        // ignore sort by service types
        if (['allServiceType', 'status', 'reportStatus', "recordType"].includes(newSortColumn)) {
            return;
        }

        let newSortOrder = SortOrder.DESC;
        if (newSortColumn === sortBy.column && sortBy.order === newSortOrder) {
            newSortOrder = SortOrder.ASC;
        }

        dispatch({type: 'SET_SORT_BY', sortBy: {column: newSortColumn, order: newSortOrder}});
    }

    let totalAuthorizations: number = 0;
    if (data?.authorizationsQuery?.pageInfo?.endCursor !== null) {
        totalAuthorizations = data?.authorizationsQuery?.totalElements ?? 0;
    }
    const {onPaginationClick} = useRelayPagination(dispatch, totalAuthorizations);

    if (error) {
        return <ErrorDisplay error={error} title="Error loading authorizations" />;
    }

    return (
        <Pane className="h-full pt-5 m-5 bg-white shadow-md flex flex-col" minHeight="calc(100vh - 100px - 260px - 40px)">
            <Pane display='flex' justifyContent='flex-end' flex="0 0" paddingBottom={20}>
                <div className="flex">

                    <NavigationSearchBar placeholder={"Search by Number or Client Name"}
                                         searchQuery={searchText}
                                         setSearchQuery={(search) => dispatch({
                                             type: 'SET_SEARCH',
                                             search: search
                                         })}/>
                    <AuthorizationListFilter initFilterValue={filterValue} dispatch={dispatch}/>

                    <div className="mr-3 flex items-center">
                        <ExportButton
                            onClick={handleExport}
                            disabled={isExporting}
                            minWidth="7rem"
                        />
                        <ExportRequestModal guid={exportGuid} onClose={() => setExportGuid(null)}/>
                    </div>

                    <SimpleDropdown options={["10", "50", "100"]} selected={pageSize.toString()}
                                    minWidth={"2rem"}
                                    textColor={"gray-700"}
                                    setSelected={(newPageSize) => dispatch({
                                        type: "SET_PAGE_SIZE",
                                        pageSize: parseInt(newPageSize)
                                    })}/>
                    <RelayPagination pageInfo={data?.authorizationsQuery.pageInfo} count={totalAuthorizations}
                                     onClick={onPaginationClick}/>
                </div>
            </Pane>
            <AuthorizationListFilterBadge filter={filterValue} dispatch={dispatch}/>
            <div className="overflow-auto flex flex-col flex-grow mb-4 scrollbar-always">
                <table className="table-fixed w-full">
                    <thead>
                    <tr className='text-left select-none text-sm text-gray-portal-table-title'>
                        {
                            AuthorizationListColumn.map((column) => (
                                <th
                                    key={column.attr}
                                    className={'font-light' + (column.attr === 'bhpnAuthNumber' ? ' pl-8' : '')}
                                    onClick={() => setSortByColumn(column.attr)}
                                    colSpan={column.span}
                                >
                                    <div className='flex items-center'>
                                        <div>
                                            {column.name}
                                        </div>
                                        <div className='w-6 h-6 pl-2'>
                                            {sortBy.column === column.attr && sortBy.order === SortOrder.ASC &&
                                                <Icon className='align-middle' icon={CaretUpIcon}/>}
                                            {sortBy.column === column.attr && sortBy.order === SortOrder.DESC &&
                                                <Icon className='align-middle' icon={CaretDownIcon}/>}
                                        </div>
                                    </div>
                                </th>
                            ))
                        }
                    </tr>
                    </thead>
                    <tbody>
                    {
                        !loading && data!.authorizationsQuery.edges.map((edge) => edge.node).map((authorization) => (
                            <AuthorizationPreview
                                key={authorization.guid}
                                authorization={authorization}
                                selected={selectedAuthorization?.guid === authorization.guid}
                                dispatch={dispatch}
                            />
                        ))
                    }
                    </tbody>
                </table>
                {
                    loading &&
                    <div className="w-full h-full flex flex-grow items-center">
                        <LoadingSpinner spacing={20} height={"100%"}/>
                    </div>
                }
                {
                    totalAuthorizations === 0 && !loading && <div className="w-full h-full flex flex-grow items-center">
                        <div className="m-auto">
                            No authorizations matched the searching criteria.
                        </div>
                    </div>
                }
            </div>
        </Pane>
    );
}
